import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import { extractYoutubeId } from './extractYoutubeId';
import { proxyEmbedUrl } from './proxyEmbedUrl';
import { parseProxyEvent, stateToAction, thumbnailUrl } from './youtubeBridge';
const PROXY_ORIGIN = new URL(__BASE_URL__).origin;
const dimension = (value)=>value == null || value === 'none' ? '100%' : typeof value === 'number' ? "".concat(value, "px") : value;
export const NativeYoutubePlayer = (param)=>{
    let { url, controls, playing, light, width, height, className, testId, title, onStart, onPlay, onPause } = param;
    const iframeRef = useRef(null);
    const hasStarted = useRef(false);
    const autoplayAtMount = useRef(playing === true);
    const unmuted = useRef(false);
    const isMountRun = useRef(true);
    const videoId = extractYoutubeId(url);
    const src = useMemo(()=>proxyEmbedUrl(__BASE_URL__, url, {
            controls,
            autoplay: autoplayAtMount.current,
            mute: autoplayAtMount.current
        }), [
        url,
        controls
    ]);
    const command = (name)=>{
        var _iframeRef_current_contentWindow, _iframeRef_current;
        return (_iframeRef_current = iframeRef.current) === null || _iframeRef_current === void 0 ? void 0 : (_iframeRef_current_contentWindow = _iframeRef_current.contentWindow) === null || _iframeRef_current_contentWindow === void 0 ? void 0 : _iframeRef_current_contentWindow.postMessage({
            source: 'yt-proxy-command',
            command: name
        }, PROXY_ORIGIN);
    };
    useEffect(()=>{
        const onMessage = (event)=>{
            if (event.origin !== PROXY_ORIGIN) return;
            const proxyEvent = parseProxyEvent(event.data);
            if (proxyEvent == null) return;
            if (proxyEvent.event === 'ready' && playing != null && !autoplayAtMount.current) {
                command(playing ? 'playVideo' : 'pauseVideo');
            }
            if (proxyEvent.event === 'state') {
                const action = stateToAction(proxyEvent.data);
                if (action === 'play') {
                    if (!hasStarted.current) {
                        hasStarted.current = true;
                        onStart === null || onStart === void 0 ? void 0 : onStart();
                    }
                    onPlay === null || onPlay === void 0 ? void 0 : onPlay();
                }
                if (action === 'pause') onPause === null || onPause === void 0 ? void 0 : onPause();
            }
        };
        window.addEventListener('message', onMessage);
        return ()=>window.removeEventListener('message', onMessage);
    }, [
        onStart,
        onPlay,
        onPause,
        playing
    ]);
    useEffect(()=>{
        if (playing == null) return;
        // mount already encodes autoplay+mute in src; unmuting here would be a gesture-less unmute (iOS blocks autoplay)
        if (isMountRun.current) {
            isMountRun.current = false;
            return;
        }
        if (autoplayAtMount.current && !unmuted.current) {
            unmuted.current = true;
            command('unMute');
        }
        command(playing ? 'playVideo' : 'pauseVideo');
    }, [
        playing
    ]);
    if (light === true && videoId != null) {
        return /*#__PURE__*/ _jsx("img", {
            className: className,
            src: thumbnailUrl(videoId),
            alt: title !== null && title !== void 0 ? title : '',
            style: {
                width: dimension(width),
                height: dimension(height)
            },
            "data-testid": testId
        });
    }
    if (src == null) return null;
    return /*#__PURE__*/ _jsx("iframe", {
        ref: iframeRef,
        className: className,
        src: src,
        title: title !== null && title !== void 0 ? title : 'video',
        "data-testid": testId,
        style: {
            width: dimension(width),
            height: dimension(height),
            border: 0
        },
        allow: "autoplay; encrypted-media; picture-in-picture; fullscreen",
        allowFullScreen: true
    });
};
